import {Action, handleActions} from "redux-actions";
import {PortfolioActions} from "./PortfolioActions";
import {ISelectValue} from "../common/Select.component";
import {PortfolioTab} from "./PortfolioPage";
import {PerformanceType} from "./portfolioUtil";

export interface IPortfolioPlanTreeState {
    expandedRows: number[];
    selectedDate?: ISelectValue;
    selectedProduct?: number;
}

export interface IPortfolioPerformanceState extends IPortfolioPlanTreeState {
    performanceType?: PerformanceType;
}

export interface IPortfolioPageState {
    currentTab?: PortfolioTab
    assetAllocationState: IPortfolioPlanTreeState;
    performanceState: IPortfolioPerformanceState;
    feesState: IPortfolioPlanTreeState;
}

export const portfolioPlanTreeInitialState: IPortfolioPlanTreeState = {
    expandedRows: [],
    selectedDate: undefined,
    selectedProduct: undefined
};

export const portfolioPerformanceInitialState: IPortfolioPerformanceState = {
    performanceType: undefined,
    expandedRows: [],
    selectedDate: undefined,
    selectedProduct: undefined
};

export const portfolioPageInitialState: IPortfolioPageState = {
    currentTab: undefined,
    assetAllocationState: portfolioPlanTreeInitialState,
    performanceState: portfolioPerformanceInitialState,
    feesState: portfolioPlanTreeInitialState
};

const updateAssetAllocationExpandedRows =
    (state: IPortfolioPageState, action: Action<number[]>): IPortfolioPageState => {
        return {
            ...state,
            assetAllocationState: {
                ...state.assetAllocationState,
                expandedRows: action.payload
                    ? action.payload
                    : portfolioPageInitialState.assetAllocationState.expandedRows
            }
        };
    };

const setAssetAllocationDate =
    (state: IPortfolioPageState, action: Action<ISelectValue>): IPortfolioPageState => {
        return {
            ...state,
            assetAllocationState: {
                ...state.assetAllocationState,
                selectedDate: action.payload
                    ? action.payload
                    : portfolioPageInitialState.assetAllocationState.selectedDate
            }
        };
    };

const setAssetAllocationSelectedProduct =
    (state: IPortfolioPageState, action: Action<number>): IPortfolioPageState => {
        return {
            ...state,
            assetAllocationState: {
                ...state.assetAllocationState,
                selectedProduct: action.payload
                    ? action.payload
                    : portfolioPageInitialState.assetAllocationState.selectedProduct
            }
        };
    };

const setCurrentTab = (state: IPortfolioPageState, action: Action<PortfolioTab>): IPortfolioPageState => {
    return {
        ...state,
        currentTab: action.payload ? action.payload : portfolioPageInitialState.currentTab
    };
};

const updatePerformanceExpandedRows = (state: IPortfolioPageState, action: Action<number[]>): IPortfolioPageState => {
    return {
        ...state,
        performanceState: {
            ...state.performanceState,
            expandedRows: action.payload
                ? action.payload
                : portfolioPageInitialState.performanceState.expandedRows
        }
    };
};

const setPerformanceDate = (state: IPortfolioPageState, action: Action<ISelectValue>): IPortfolioPageState => {
    return {
        ...state,
        performanceState: {
            ...state.performanceState,
            selectedDate: action.payload
                ? action.payload
                : portfolioPageInitialState.performanceState.selectedDate
        }
    };
};

const setPerformanceSelectedProduct = (state: IPortfolioPageState, action: Action<number>): IPortfolioPageState => {
    return {
        ...state,
        performanceState: {
            ...state.performanceState,
            selectedProduct: action.payload
                ? action.payload
                : portfolioPageInitialState.performanceState.selectedProduct
        }
    };
};

const setPerformanceType = (state: IPortfolioPageState, action: Action<PerformanceType>): IPortfolioPageState => {
    return {
        ...state,
        performanceState: {
            ...state.performanceState,
            performanceType: action.payload
                ? action.payload
                : portfolioPageInitialState.performanceState.performanceType
        }
    };
};

const updateFeesExpandedRows = (state: IPortfolioPageState, action: Action<number[]>): IPortfolioPageState => {
    return {
        ...state,
        feesState: {
            ...state.feesState,
            expandedRows: action.payload
                ? action.payload
                : portfolioPageInitialState.feesState.expandedRows
        }
    };
};

const setFeesSelectedProduct = (state: IPortfolioPageState, action: Action<number>): IPortfolioPageState => {
    return {
        ...state,
        feesState: {
            ...state.feesState,
            selectedProduct: action.payload
                ? action.payload
                : portfolioPageInitialState.feesState.selectedProduct
        }
    };
};

const reducerMap = {
    [PortfolioActions.SET_ASSET_ALLOCATION_SELECTED_DATE]: setAssetAllocationDate,
    [PortfolioActions.UPDATE_ASSET_ALLOCATION_EXPANDED_ROWS]: updateAssetAllocationExpandedRows,
    [PortfolioActions.SET_ASSET_ALLOCATION_SELECTED_PRODUCT]: setAssetAllocationSelectedProduct,
    [PortfolioActions.SET_CURRENT_TAB]: setCurrentTab,
    [PortfolioActions.UPDATE_PERFORMANCE_EXPANDED_ROWS]: updatePerformanceExpandedRows,
    [PortfolioActions.SET_PERFORMANCE_SELECTED_DATE]: setPerformanceDate,
    [PortfolioActions.SET_PERFORMANCE_SELECTED_PRODUCT]: setPerformanceSelectedProduct,
    [PortfolioActions.SET_PERFORMANCE_TYPE]: setPerformanceType,
    [PortfolioActions.UPDATE_FEES_EXPANDED_ROWS]: updateFeesExpandedRows,
    [PortfolioActions.SET_FEES_SELECTED_PRODUCT]: setFeesSelectedProduct,
};

export function assetAllocationExpandedRowsInner(state?: IPortfolioPageState): number[] {
    return state ? state.assetAllocationState.expandedRows : [];
}

export function assetAllocationSelectedDateInner(state?: IPortfolioPageState): ISelectValue | undefined {
    return state ? state.assetAllocationState.selectedDate : undefined;
}

export function assetAllocationSelectedProductInner(state?: IPortfolioPageState): number | undefined {
    return state ? state.assetAllocationState.selectedProduct : undefined;
}

export function portfolioPageCurrentTabInner(state?: IPortfolioPageState): PortfolioTab | undefined{
    return state ? state.currentTab : portfolioPageInitialState.currentTab;
}

export function performanceExpandedRowsInner(state?: IPortfolioPageState): number[] {
    return state ? state.performanceState.expandedRows : [];
}

export function performanceSelectedDateInner(state?: IPortfolioPageState): ISelectValue | undefined {
    return state ? state.performanceState.selectedDate : undefined;
}

export function performanceSelectedProductInner(state?: IPortfolioPageState): number | undefined {
    return state ? state.performanceState.selectedProduct : undefined;
}

export function performanceTypeInner(state?: IPortfolioPageState): PerformanceType | undefined {
    return state ? state.performanceState.performanceType : undefined;
}

export function feesExpandedRowsInner(state?: IPortfolioPageState): number[] {
    return state ? state.feesState.expandedRows : [];
}

export function feesSelectedProductInner(state?: IPortfolioPageState): number | undefined {
    return state ? state.feesState.selectedProduct : undefined;
}

type portfolioActionTypes = number[] | ISelectValue | number | undefined | PortfolioTab;

export default handleActions<IPortfolioPageState, portfolioActionTypes>(reducerMap, portfolioPageInitialState);